import { useState, useEffect } from 'react';
import {
  Package,
  Loader2,
  Edit2,
  Copy,
  Printer,
  Trash2,
  History,
  Layers,
  ClipboardCheck,
  ArrowUpDown,
  ArrowRightLeft,
  Eye,
  EyeOff,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { supabase } from '@/lib/supabase';
import { useCurrency } from '@/hooks/useCurrency';
import { useLocations } from '@/hooks/useLocations';
import type { InventoryItem } from '@/hooks/useInventory';
import type { Product } from '@/hooks/useProducts';

// Multi-location stock data
interface MultiLocationStock {
  location_id: string;
  location_name: string;
  quantity_on_hand: number;
  quantity_boh: number;
  quantity_foh: number;
  inbound_quantity: number;
  items_sold_30_days: number;
}

// Composite component stock across locations
interface ComponentLocationStock {
  component_name: string;
  component_sku: string | null;
  quantity: number;
  is_raw: boolean;
  stock_by_location: Record<string, number>;
}

const transactionTypeColors: Record<string, string> = {
  received: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  received_boh: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  sold: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  adjusted: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  transferred: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
  transfer_boh_to_foh: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
  transfer_foh_to_boh: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400',
  returned: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
  waste: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  consumed: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
};

// Actions that the parent page can provide
export interface ExpandedProductRowActions {
  onEdit: () => void;
  onDuplicate?: () => void;
  onPrintLabels?: () => void;
  onViewMovements?: () => void;
  onDelete?: () => void;
  // Inventory-specific actions (from InventoryOverview)
  onAdjustInventory?: () => void;
  onStockCount?: () => void;
  onTransfer?: () => void;
  onToggle86d?: () => void;
  canTransfer?: boolean;
  canEdit?: boolean;
  canDelete?: boolean;
}

// The component accepts EITHER an InventoryItem or a Product
export interface ExpandedProductRowProps {
  // Provide one of these:
  inventoryItem?: InventoryItem;
  product?: Product;
  // The product ID to use for fetching data
  productId: string;
  // Column span for the table row
  colSpan: number;
  // Actions
  actions: ExpandedProductRowActions;
}

export function ExpandedProductRow({
  inventoryItem,
  product,
  productId,
  colSpan,
  actions,
}: ExpandedProductRowProps) {
  const [activeTab, setActiveTab] = useState<'inventory' | 'pricing' | 'history' | 'details' | 'associated'>('inventory');
  const [multiLocationStock, setMultiLocationStock] = useState<MultiLocationStock[]>([]);
  const [compositeComponents, setCompositeComponents] = useState<ComponentLocationStock[]>([]);
  const [locationNames, setLocationNames] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const { formatCurrency } = useCurrency();
  const { locations: orgLocations } = useLocations();

  // Resolve product data from either source
  const productData = inventoryItem?.product || (product ? {
    id: product.id,
    name: product.name,
    sku: product.sku,
    barcode: product.barcode,
    price: product.price,
    cost: product.cost,
    image_url: product.image_url,
    is_active: product.is_active,
    is_available: product.is_available,
    is_sellable: product.is_sellable,
    category_id: product.category_id,
    product_type: product.product_type,
    tax_rate: product.tax_rate,
    description: product.description,
  } : null);

  const isComposite = productData?.product_type === 'composite' || productData?.product_type === 'composite_variant';
  const isCompositeVariant = productData?.product_type === 'composite_variant';
  const [variantData, setVariantData] = useState<{ id: string; name: string; price: number | null }[]>([]);

  // Associated products: composites that use this product as a component
  const [associatedProducts, setAssociatedProducts] = useState<{
    parentId: string;
    parentName: string;
    parentImage: string | null;
    parentType: string | null;
    qtyPerUnit: number;
  }[]>([]);
  const [loadingAssociated, setLoadingAssociated] = useState(false);
  const [compositeVirtualQty, setCompositeVirtualQty] = useState<number | null>(null);
  const [compositeVirtualByLocation, setCompositeVirtualByLocation] = useState<Record<string, number>>({});

  useEffect(() => {
    fetchMultiLocationStock();
  }, [productId]);

  const fetchMultiLocationStock = async () => {
    setLoading(true);
    try {
      // Fetch inventory across locations
      const { data, error } = await supabase
        .from('inventory_items')
        .select(`
          id,
          location_id,
          quantity_on_hand,
          quantity_boh,
          quantity_foh,
          location:locations(id, name)
        `)
        .eq('product_id', productId);

      if (error) throw error;

      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const inventoryIds = data?.map((d: any) => d.id) || [];

      let salesByItem: Record<string, number> = {};
      if (inventoryIds.length > 0) {
        const { data: salesData } = await supabase
          .from('inventory_transactions')
          .select('inventory_item_id, quantity_change')
          .eq('transaction_type', 'sold')
          .in('inventory_item_id', inventoryIds)
          .gte('created_at', thirtyDaysAgo.toISOString());

        salesByItem = (salesData || []).reduce((acc: Record<string, number>, txn: any) => {
          acc[txn.inventory_item_id] = (acc[txn.inventory_item_id] || 0) + Math.abs(txn.quantity_change || 0);
          return acc;
        }, {});
      }

      const result: MultiLocationStock[] = (data || []).map((d: any) => ({
        location_id: d.location_id,
        location_name: d.location?.name || 'Unknown',
        quantity_on_hand: d.quantity_on_hand || 0,
        quantity_boh: d.quantity_boh || 0,
        quantity_foh: d.quantity_foh ?? d.quantity_on_hand ?? 0,
        inbound_quantity: 0,
        items_sold_30_days: salesByItem[d.id] || 0,
      }));

      setMultiLocationStock(result);
      // Start with all org locations, then fill in any extras from stock data
      const allOrgLocNames = orgLocations.map(l => l.name);
      const stockLocNames = result.map(r => r.location_name);
      // Use org locations as the baseline, add any stock-only locations not in the list
      const locNames = [...allOrgLocNames];
      for (const sn of stockLocNames) {
        if (!locNames.includes(sn)) locNames.push(sn);
      }
      setLocationNames(locNames);

      // Fetch composite components with per-location stock if this is a composite product
      if (isComposite) {
        if (isCompositeVariant) {
          const { data: variants } = await supabase
            .from('product_variants')
            .select('id, name, price')
            .eq('product_id', productId)
            .eq('is_active', true)
            .order('sort_order');
          setVariantData(variants || []);
        }

        const { data: components, error: compError } = await supabase
          .from('composite_product_components')
          .select(`
            id,
            quantity,
            variant_id,
            component_product:products!component_product_id(id, name, sku),
            raw_item:raw_inventory_items!raw_item_id(id, name, sku)
          `)
          .eq('parent_product_id', productId)
          .is('variant_id', null);

        if (!compError && components) {
          const compStocks: ComponentLocationStock[] = [];

          for (const comp of components) {
            const cp = comp.component_product as any;
            const ri = comp.raw_item as any;
            const compName = cp?.name || ri?.name || 'Unknown';
            const compSku = cp?.sku || ri?.sku || null;
            const isRaw = !!ri;

            const stockByLoc: Record<string, number> = {};

            if (cp?.id) {
              const { data: compInv } = await supabase
                .from('inventory_items')
                .select('location_id, quantity_on_hand, location:locations(name)')
                .eq('product_id', cp.id);

              for (const ci of compInv || []) {
                const locName = (ci.location as any)?.name || 'Unknown';
                stockByLoc[locName] = ci.quantity_on_hand || 0;
              }
            } else if (ri?.id) {
              const { data: rawStock } = await supabase
                .from('raw_inventory_stock')
                .select('location_id, quantity_on_hand, location:locations(name)')
                .eq('inventory_item_id', ri.id);

              for (const rs of rawStock || []) {
                const locName = (rs.location as any)?.name || 'Unknown';
                stockByLoc[locName] = rs.quantity_on_hand || 0;
              }
            }

            compStocks.push({
              component_name: compName,
              component_sku: compSku,
              quantity: comp.quantity,
              is_raw: isRaw,
              stock_by_location: stockByLoc,
            });
          }

          setCompositeComponents(compStocks);
        }

        // Fetch the virtual quantity (how many can be made) per location
        const { data: virtualInv } = await supabase
          .from('composite_product_virtual_inventory')
          .select('virtual_quantity, location_id')
          .eq('product_id', productId)
          .is('variant_id', null);

        if (virtualInv && virtualInv.length > 0) {
          // Fetch location names for virtual inventory locations
          const vLocIds = [...new Set(virtualInv.map(v => v.location_id))];
          const { data: vLocData } = await supabase
            .from('locations')
            .select('id, name')
            .in('id', vLocIds);
          const vLocNameMap = new Map((vLocData || []).map((l: any) => [l.id, l.name]));

          // Build per-location virtual quantities
          const virtualByLoc: Record<string, number> = {};
          let totalVirtual = 0;
          for (const v of virtualInv) {
            const vLocName = vLocNameMap.get(v.location_id) || 'Unknown';
            virtualByLoc[vLocName] = v.virtual_quantity ?? 0;
            totalVirtual += v.virtual_quantity ?? 0;
          }
          setCompositeVirtualQty(totalVirtual);
          setCompositeVirtualByLocation(virtualByLoc);

          // Ensure locations with virtual stock appear in the table headers
          const updatedLocNames = [...locNames];
          for (const vLocName of Object.keys(virtualByLoc)) {
            if (!updatedLocNames.includes(vLocName)) {
              updatedLocNames.push(vLocName);
            }
          }
          if (updatedLocNames.length !== locNames.length) {
            setLocationNames(updatedLocNames);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching multi-location stock:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    if (transactions.length > 0) return;
    setLoadingHistory(true);
    try {
      // If we have an inventory item, fetch transactions for it
      // Otherwise fetch all transactions for this product across locations
      let query = supabase
        .from('inventory_transactions')
        .select(`
          id,
          created_at,
          transaction_type,
          quantity_change,
          quantity_before,
          quantity_after,
          notes,
          user:profiles(first_name, last_name)
        `)
        .order('created_at', { ascending: false })
        .limit(20);

      if (inventoryItem) {
        query = query.eq('inventory_item_id', inventoryItem.id);
      } else {
        // Get all inventory item IDs for this product, then filter
        const { data: invItems } = await supabase
          .from('inventory_items')
          .select('id')
          .eq('product_id', productId);
        
        if (invItems && invItems.length > 0) {
          query = query.in('inventory_item_id', invItems.map(i => i.id));
        } else {
          setTransactions([]);
          setLoadingHistory(false);
          return;
        }
      }

      const { data, error } = await query;
      if (!error && data) {
        setTransactions(data);
      }
    } catch (err) {
      console.error('Error fetching history:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  const fetchAssociatedProducts = async () => {
    if (associatedProducts.length > 0) return;
    setLoadingAssociated(true);
    try {
      const { data, error } = await supabase
        .from('composite_product_components')
        .select(`
          quantity,
          parent_product:products!parent_product_id(id, name, image_url, product_type)
        `)
        .eq('component_product_id', productId);

      if (error) throw error;

      const results = (data || [])
        .filter((row: any) => row.parent_product)
        .map((row: any) => {
          const parent = Array.isArray(row.parent_product) ? row.parent_product[0] : row.parent_product;
          return {
            parentId: parent.id,
            parentName: parent.name,
            parentImage: parent.image_url,
            parentType: parent.product_type,
            qtyPerUnit: row.quantity,
          };
        });

      setAssociatedProducts(results);
    } catch (err) {
      console.error('Error fetching associated products:', err);
    } finally {
      setLoadingAssociated(false);
    }
  };

  // Get all unique location names across both main stock and component stock
  const allLocNames = [...new Set([
    ...locationNames,
    ...compositeComponents.flatMap(c => Object.keys(c.stock_by_location)),
  ])];

  // Compute the total across all locations
  const computedTotal = multiLocationStock.reduce((sum, s) => sum + s.quantity_on_hand, 0);

  const tabs = [
    { id: 'inventory' as const, label: 'Inventory' },
    ...(!isComposite ? [{ id: 'associated' as const, label: 'Used In' }] : []),
    { id: 'pricing' as const, label: 'Pricing' },
    { id: 'history' as const, label: 'History' },
    { id: 'details' as const, label: 'Details' },
  ];

  return (
    <tr className="border-b bg-muted/10">
      <td colSpan={colSpan} className="p-0">
        <div className="flex flex-col md:flex-row" onClick={(e) => e.stopPropagation()}>
          {/* Left side: Content with tabs */}
          <div className="flex-1 min-w-0">
            {/* Horizontal tab bar */}
            <div className="flex border-b">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveTab(tab.id);
                    if (tab.id === 'history') fetchHistory();
                    if (tab.id === 'associated') fetchAssociatedProducts();
                  }}
                  className={cn(
                    'px-5 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap',
                    activeTab === tab.id
                      ? 'border-primary text-primary'
                      : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground/30'
                  )}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Tab content */}
            <div className="p-4">
              {activeTab === 'inventory' && (
                <div className="space-y-6">
                  {loading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                    </div>
                  ) : (
                    <>
                      {/* Product stock by location */}
                      <div className="overflow-x-auto relative">
                        <table className="text-sm min-w-max w-full">
                          <thead>
                            <tr className="border-b text-xs text-muted-foreground">
                              <th className="text-left px-3 py-2 font-medium sticky left-0 z-10 bg-background min-w-[180px]">Product</th>
                              <th className={cn(
                                "text-right px-3 py-2 font-medium sticky z-10 bg-background min-w-[80px]",
                                "left-[180px]",
                                allLocNames.length > 0 && "border-r border-border shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]"
                              )}>
                                {isComposite ? 'Can Make' : 'Total'}
                              </th>
                              {allLocNames.map((loc) => (
                                <th key={loc} className="text-right px-3 py-2 font-medium min-w-[90px] whitespace-nowrap">{loc}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            <tr className="border-b hover:bg-muted/30">
                              <td className="px-3 py-2 sticky left-0 z-10 bg-background min-w-[180px]">
                                <div className="flex items-center gap-2">
                                  <div className="w-7 h-7 rounded bg-muted flex items-center justify-center shrink-0 overflow-hidden">
                                    {productData?.image_url ? (
                                      <img src={productData.image_url} alt="" className="w-full h-full object-cover" />
                                    ) : (
                                      <Package className="h-3.5 w-3.5 text-muted-foreground" />
                                    )}
                                  </div>
                                  <div className="min-w-0">
                                    <p className="font-medium truncate">{productData?.name}</p>
                                    <p className="text-xs text-muted-foreground">{productData?.sku || 'No SKU'}</p>
                                  </div>
                                </div>
                              </td>
                              <td className={cn(
                                "px-3 py-2 text-right font-semibold tabular-nums sticky z-10 bg-background min-w-[80px]",
                                "left-[180px]",
                                allLocNames.length > 0 && "border-r border-border shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]"
                              )}>
                                {isComposite && compositeVirtualQty !== null ? (
                                  <span className={cn(
                                    compositeVirtualQty <= 0 && 'text-red-600',
                                    compositeVirtualQty > 0 && compositeVirtualQty <= 5 && 'text-yellow-600',
                                    compositeVirtualQty > 5 && 'text-green-600',
                                  )}>
                                    {compositeVirtualQty.toLocaleString()}
                                  </span>
                                ) : (
                                  computedTotal.toLocaleString()
                                )}
                              </td>
                              {allLocNames.map((locName) => {
                                if (isComposite) {
                                  // Show virtual quantity (can make) for composite products
                                  const vQty = compositeVirtualByLocation[locName] ?? 0;
                                  return (
                                    <td key={locName} className="px-3 py-2 text-right tabular-nums min-w-[90px]">
                                      <span className={cn(
                                        vQty <= 0 && 'text-red-600',
                                        vQty > 0 && vQty <= 5 && 'text-yellow-600',
                                        vQty > 5 && 'text-green-600',
                                      )}>
                                        {vQty.toLocaleString()}
                                      </span>
                                    </td>
                                  );
                                }
                                const locStock = multiLocationStock.find(s => s.location_name === locName);
                                return (
                                  <td key={locName} className="px-3 py-2 text-right tabular-nums min-w-[90px]">
                                    {locStock ? locStock.quantity_on_hand.toLocaleString() : '0'}
                                  </td>
                                );
                              })}
                            </tr>
                          </tbody>
                        </table>
                      </div>

                      {/* What's In It - Composite components (base recipe) */}
                      {isComposite && compositeComponents.length > 0 && (
                        <div>
                          <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                            {isCompositeVariant ? 'Base Recipe' : "What's In It"}
                          </h4>
                          <div className="overflow-x-auto relative">
                            <table className="text-sm min-w-max w-full">
                              <thead>
                                <tr className="border-b text-xs text-muted-foreground">
                                  <th className="text-left px-3 py-2 font-medium sticky left-0 z-10 bg-background min-w-[180px]">Product</th>
                                  <th className={cn(
                                    "text-right px-3 py-2 font-medium sticky z-10 bg-background min-w-[80px]",
                                    "left-[180px]",
                                    allLocNames.length > 0 && "border-r border-border shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]"
                                  )}>Total</th>
                                  {allLocNames.map((loc) => (
                                    <th key={loc} className="text-right px-3 py-2 font-medium min-w-[90px] whitespace-nowrap">{loc}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {compositeComponents.map((comp, idx) => {
                                  const compTotal = Object.values(comp.stock_by_location).reduce((sum, v) => sum + v, 0);
                                  return (
                                    <tr key={idx} className="border-b last:border-0 hover:bg-muted/30">
                                      <td className="px-3 py-2 sticky left-0 z-10 bg-background min-w-[180px]">
                                        <div className="flex items-center gap-2">
                                          <span className="text-xs tabular-nums text-muted-foreground w-5 shrink-0 text-center">{comp.quantity}</span>
                                          <div className={cn(
                                            "w-7 h-7 rounded flex items-center justify-center shrink-0",
                                            comp.is_raw ? "bg-amber-100 dark:bg-amber-900/30" : "bg-muted"
                                          )}>
                                            {comp.is_raw ? (
                                              <Layers className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
                                            ) : (
                                              <Package className="h-3.5 w-3.5 text-muted-foreground" />
                                            )}
                                          </div>
                                          <div className="min-w-0">
                                            <p className="font-medium truncate">{comp.component_name}</p>
                                            {comp.component_sku && (
                                              <p className="text-xs text-muted-foreground">{comp.component_sku}</p>
                                            )}
                                          </div>
                                        </div>
                                      </td>
                                      <td className={cn(
                                        "px-3 py-2 text-right font-semibold tabular-nums sticky z-10 bg-background min-w-[80px]",
                                        "left-[180px]",
                                        allLocNames.length > 0 && "border-r border-border shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]"
                                      )}>
                                        {compTotal.toLocaleString()}
                                      </td>
                                      {allLocNames.map((locName) => {
                                        const qty = comp.stock_by_location[locName] ?? 0;
                                        return (
                                          <td key={locName} className={cn(
                                            "px-3 py-2 text-right tabular-nums min-w-[90px]",
                                            qty < 0 && "text-red-600"
                                          )}>
                                            {qty.toLocaleString()}
                                          </td>
                                        );
                                      })}
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* Variants for composite_variant products */}
                      {isCompositeVariant && variantData.length > 0 && (
                        <div>
                          <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                            Variants
                          </h4>
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b text-xs text-muted-foreground">
                                <th className="text-left px-3 py-2 font-medium">Variant</th>
                                <th className="text-right px-3 py-2 font-medium">Price</th>
                              </tr>
                            </thead>
                            <tbody>
                              {variantData.map(v => (
                                <tr key={v.id} className="border-b last:border-0 hover:bg-muted/30">
                                  <td className="px-3 py-2 font-medium">{v.name}</td>
                                  <td className="px-3 py-2 text-right tabular-nums">
                                    {v.price != null ? formatCurrency(v.price) : '--'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {activeTab === 'pricing' && (
                <div className="space-y-4">
                  {/* Main product pricing */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div className="p-3 rounded-lg bg-muted/30">
                      <p className="text-muted-foreground text-xs mb-1">Sell Price</p>
                      <p className="font-semibold text-lg">{formatCurrency(productData?.price || 0)}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-muted/30">
                      <p className="text-muted-foreground text-xs mb-1">Cost Price</p>
                      <p className="font-semibold text-lg">{formatCurrency(productData?.cost || 0)}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-muted/30">
                      <p className="text-muted-foreground text-xs mb-1">Margin</p>
                      <p className={cn(
                        "font-semibold text-lg",
                        (() => {
                          const cost = productData?.cost || 0;
                          const price = productData?.price || 0;
                          if (price <= 0) return 'text-muted-foreground';
                          const margin = ((price - cost) / price) * 100;
                          if (margin < 0) return 'text-red-600';
                          if (margin >= 30) return 'text-green-600';
                          return '';
                        })()
                      )}>
                        {productData?.price && productData.price > 0
                          ? `${(((productData.price - (productData.cost || 0)) / productData.price) * 100).toFixed(1)}%`
                          : '--'}
                      </p>
                    </div>
                    <div className="p-3 rounded-lg bg-muted/30">
                      <p className="text-muted-foreground text-xs mb-1">Profit / Unit</p>
                      <p className="font-semibold text-lg">
                        {formatCurrency((productData?.price || 0) - (productData?.cost || 0))}
                      </p>
                    </div>
                  </div>
                  {productData?.tax_rate !== undefined && productData?.tax_rate !== null && (
                    <div className="text-sm text-muted-foreground">
                      Tax rate: {(productData.tax_rate * 100).toFixed(1)}%
                    </div>
                  )}

                  {/* Composite component pricing */}
                  {isComposite && compositeComponents.length > 0 && (
                    <div>
                      <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                        {"What's In It"}
                      </h4>
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b text-xs text-muted-foreground">
                            <th className="text-left px-3 py-2 font-medium w-16">Qty</th>
                            <th className="text-left px-3 py-2 font-medium">Product</th>
                          </tr>
                        </thead>
                        <tbody>
                          {compositeComponents.map((comp, idx) => (
                            <tr key={idx} className="border-b last:border-0 hover:bg-muted/30">
                              <td className="px-3 py-2 tabular-nums">{comp.quantity}</td>
                              <td className="px-3 py-2">
                                <div className="flex items-center gap-2">
                                  <div className={cn(
                                    "w-7 h-7 rounded flex items-center justify-center shrink-0",
                                    comp.is_raw ? "bg-amber-100 dark:bg-amber-900/30" : "bg-muted"
                                  )}>
                                    {comp.is_raw ? (
                                      <Layers className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
                                    ) : (
                                      <Package className="h-3.5 w-3.5 text-muted-foreground" />
                                    )}
                                  </div>
                                  <div className="min-w-0">
                                    <p className="font-medium truncate">{comp.component_name}</p>
                                    {comp.component_sku && (
                                      <p className="text-xs text-muted-foreground">{comp.component_sku}</p>
                                    )}
                                  </div>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'history' && (
                <div>
                  {loadingHistory ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                    </div>
                  ) : transactions.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-8">No transaction history</p>
                  ) : (
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b text-xs text-muted-foreground">
                          <th className="text-left px-3 py-2 font-medium">Date</th>
                          <th className="text-left px-3 py-2 font-medium">Type</th>
                          <th className="text-right px-3 py-2 font-medium">Change</th>
                          <th className="text-right px-3 py-2 font-medium">After</th>
                          <th className="text-left px-3 py-2 font-medium">User</th>
                          <th className="text-left px-3 py-2 font-medium">Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {transactions.map((txn: any) => {
                          const user = txn.user as any;
                          return (
                            <tr key={txn.id} className="border-b last:border-0 hover:bg-muted/30">
                              <td className="px-3 py-2 whitespace-nowrap">
                                {new Date(txn.created_at).toLocaleDateString('en-US', {
                                  month: 'short', day: 'numeric', year: 'numeric',
                                })}
                              </td>
                              <td className="px-3 py-2">
                                <Badge variant="outline" className={cn('text-xs', transactionTypeColors[txn.transaction_type] || '')}>
                                  {txn.transaction_type}
                                </Badge>
                              </td>
                              <td className={cn(
                                "px-3 py-2 text-right tabular-nums font-medium",
                                txn.quantity_change > 0 ? 'text-green-600' : txn.quantity_change < 0 ? 'text-red-600' : ''
                              )}>
                                {txn.quantity_change > 0 ? '+' : ''}{txn.quantity_change}
                              </td>
                              <td className="px-3 py-2 text-right tabular-nums">{txn.quantity_after}</td>
                              <td className="px-3 py-2 text-muted-foreground">
                                {user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() : '--'}
                              </td>
                              <td className="px-3 py-2 text-muted-foreground truncate max-w-[200px]">
                                {txn.notes || '--'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              )}

              {activeTab === 'details' && (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground text-xs mb-1">Product Name</p>
                    <p className="font-medium">{productData?.name || '--'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs mb-1">SKU</p>
                    <p className="font-medium">{productData?.sku || '--'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs mb-1">Barcode</p>
                    <p className="font-medium">{productData?.barcode || '--'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs mb-1">Product Type</p>
                    <p className="font-medium capitalize">{productData?.product_type || 'standard'}</p>
                  </div>
                  {inventoryItem && (
                    <>
                      <div>
                        <p className="text-muted-foreground text-xs mb-1">Reorder Point</p>
                        <p className="font-medium">{inventoryItem.reorder_point ?? '--'}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs mb-1">Reorder Qty</p>
                        <p className="font-medium">{inventoryItem.reorder_quantity ?? '--'}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs mb-1">Last Counted</p>
                        <p className="font-medium">
                          {inventoryItem.last_counted_at
                            ? new Date(inventoryItem.last_counted_at).toLocaleDateString('en-US', {
                                month: 'short', day: 'numeric', year: 'numeric',
                              })
                            : 'Never'}
                        </p>
                      </div>
                    </>
                  )}
                  {product && (
                    <div>
                      <p className="text-muted-foreground text-xs mb-1">Created</p>
                      <p className="font-medium">
                        {new Date(product.created_at).toLocaleDateString('en-US', {
                          month: 'short', day: 'numeric', year: 'numeric',
                        })}
                      </p>
                    </div>
                  )}
                  {inventoryItem && (
                    <div>
                      <p className="text-muted-foreground text-xs mb-1">Created</p>
                      <p className="font-medium">
                        {new Date(inventoryItem.created_at).toLocaleDateString('en-US', {
                          month: 'short', day: 'numeric', year: 'numeric',
                        })}
                      </p>
                    </div>
                  )}
                  {productData?.description && (
                    <div className="col-span-full">
                      <p className="text-muted-foreground text-xs mb-1">Description</p>
                      <p className="font-medium">{productData.description}</p>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'associated' && (
                <div className="space-y-3">
                  {loadingAssociated ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                    </div>
                  ) : associatedProducts.length === 0 ? (
                    <div className="py-8 text-center text-sm text-muted-foreground">
                      This item is not used as a component in any composite products.
                    </div>
                  ) : (
                    <>
                      <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        Composite products using this item ({associatedProducts.length})
                      </h4>
                      <div className="overflow-x-auto">
                        <table className="text-sm w-full">
                          <thead>
                            <tr className="border-b text-xs text-muted-foreground">
                              <th className="text-left px-3 py-2 font-medium">Product</th>
                              <th className="text-left px-3 py-2 font-medium">Type</th>
                              <th className="text-right px-3 py-2 font-medium">Qty per Unit</th>
                            </tr>
                          </thead>
                          <tbody>
                            {associatedProducts.map((ap) => (
                              <tr key={ap.parentId} className="border-b last:border-0 hover:bg-muted/30">
                                <td className="px-3 py-2">
                                  <div className="flex items-center gap-2">
                                    <div className="w-7 h-7 rounded bg-muted flex items-center justify-center shrink-0 overflow-hidden">
                                      {ap.parentImage ? (
                                        <img src={ap.parentImage} alt="" className="w-full h-full object-cover" />
                                      ) : (
                                        <Package className="h-3.5 w-3.5 text-muted-foreground" />
                                      )}
                                    </div>
                                    <span className="font-medium">{ap.parentName}</span>
                                  </div>
                                </td>
                                <td className="px-3 py-2">
                                  <Badge variant="outline" className="text-[10px] capitalize">
                                    {ap.parentType?.replace('_', ' ') || 'composite'}
                                  </Badge>
                                </td>
                                <td className="px-3 py-2 text-right tabular-nums font-medium">
                                  {ap.qtyPerUnit}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Right side: Action buttons column */}
          <div className="w-full md:w-auto shrink-0 border-t md:border-t-0 md:border-l p-3 md:p-4 flex md:flex-col flex-wrap gap-2">
            {actions.canEdit !== false && (
              <Button
                variant="blaze"
                size="sm"
                className="w-full justify-start gap-2 whitespace-nowrap"
                onClick={(e) => { e.stopPropagation(); actions.onEdit(); }}
              >
                <Edit2 className="h-4 w-4 shrink-0" />
                Edit
              </Button>
            )}
            {actions.onStockCount && (
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start gap-2 whitespace-nowrap"
                onClick={(e) => { e.stopPropagation(); actions.onStockCount!(); }}
              >
                <ClipboardCheck className="h-4 w-4 shrink-0" />
                Stock Count
              </Button>
            )}
            {actions.onAdjustInventory && (
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start gap-2 whitespace-nowrap"
                onClick={(e) => { e.stopPropagation(); actions.onAdjustInventory!(); }}
              >
                <ArrowUpDown className="h-4 w-4 shrink-0" />
                Adjust inventory
              </Button>
            )}
            {actions.onTransfer && actions.canTransfer && (
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start gap-2 whitespace-nowrap text-purple-600 hover:text-purple-700"
                onClick={(e) => { e.stopPropagation(); actions.onTransfer!(); }}
              >
                <ArrowRightLeft className="h-4 w-4 shrink-0" />
                Transfer BOH/FOH
              </Button>
            )}
            {actions.onPrintLabels && (
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start gap-2 whitespace-nowrap"
                onClick={(e) => { e.stopPropagation(); actions.onPrintLabels!(); }}
              >
                <Printer className="h-4 w-4 shrink-0" />
                Print labels...
              </Button>
            )}
            {actions.onDuplicate && (
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start gap-2 whitespace-nowrap"
                onClick={(e) => { e.stopPropagation(); actions.onDuplicate!(); }}
              >
                <Copy className="h-4 w-4 shrink-0" />
                Duplicate
              </Button>
            )}
            {actions.onViewMovements && (
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start gap-2 whitespace-nowrap"
                onClick={(e) => { e.stopPropagation(); actions.onViewMovements!(); }}
              >
                <History className="h-4 w-4 shrink-0" />
                View inventory movements
              </Button>
            )}
            {actions.onToggle86d && (
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start gap-2 whitespace-nowrap"
                onClick={(e) => { e.stopPropagation(); actions.onToggle86d!(); }}
              >
                {(product?.is_available ?? productData?.is_available) ? (
                  <>
                    <EyeOff className="h-4 w-4 shrink-0" />
                    86 item
                  </>
                ) : (
                  <>
                    <Eye className="h-4 w-4 shrink-0" />
                    Make available
                  </>
                )}
              </Button>
            )}
            {actions.onDelete && actions.canDelete !== false && (
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start gap-2 whitespace-nowrap text-red-600 hover:text-red-700 hover:bg-red-50"
                onClick={(e) => { e.stopPropagation(); actions.onDelete!(); }}
              >
                <Trash2 className="h-4 w-4 shrink-0" />
                Delete
              </Button>
            )}
          </div>
        </div>
      </td>
    </tr>
  );
}
